// Copyright 2021-2026 - Hewlett Packard Enterprise Company
import React, { createContext, useState } from 'react';
import CONTENT_IDS from '../constants/helpContentIds';

export interface HelpContextValue {
  contentId: string;
  setContentId: (id: string) => void;
  isHelpOpen: boolean;
  setIsHelpOpen: (
    isOpen: boolean,
    options?: { keepNotificationCenter?: boolean },
  ) => void;
  isNotificationCenterOpen: boolean;
  setIsNotificationCenterOpen: (isOpen: boolean) => void;
}

const defaultContextValue: HelpContextValue = {
  contentId: CONTENT_IDS.ANNOUNCEMENTS ?? '',
  setContentId: () => {},
  isHelpOpen: false,
  setIsHelpOpen: () => {},
  isNotificationCenterOpen: false,
  setIsNotificationCenterOpen: () => {},
};

export const HelpContext = createContext<HelpContextValue>(defaultContextValue);

interface Props {
  children: React.ReactNode;
}

export default function HelpProvider({ children }: Props): React.JSX.Element {
  const [contentId, setContentId] = useState(CONTENT_IDS.ANNOUNCEMENTS ?? '');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] =
    useState(false);

  return (
    <HelpContext.Provider
      value={{
        contentId,
        setContentId,
        isHelpOpen,
        setIsHelpOpen: (isOpen, { keepNotificationCenter = false } = {}) => {
          if (isOpen && !keepNotificationCenter) {
            setIsNotificationCenterOpen(false);
          }
          setIsHelpOpen(isOpen);
        },
        isNotificationCenterOpen,
        setIsNotificationCenterOpen: isOpen => {
          setIsNotificationCenterOpen(isOpen);
          if (isOpen) {
            setIsHelpOpen(!isOpen);
          }
        },
      }}
    >
      {children}
    </HelpContext.Provider>
  );
}
